import { useEffect, useMemo, useRef, useState } from "react";
import { stories } from "../../../mocks/stories";

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
  onOpenStory: (id: number) => void;
}

export default function SearchModal({ open, onClose, onOpenStory }: SearchModalProps) {
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    setQ("");
    window.setTimeout(() => inputRef.current?.focus(), 60);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const results = useMemo(() => {
    const k = q.trim();
    if (!k) return stories.slice(0, 4);
    return stories.filter((s) => (s.title + s.snippet + s.tag).toLowerCase().includes(k.toLowerCase())).slice(0, 6);
  }, [q]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 pt-24 sm:pt-28">
      <div className="absolute inset-0 bg-foreground-950/60 animate-fade" onClick={onClose}></div>
      <div className="relative w-full max-w-xl overflow-hidden rounded-3xl bg-background-50 shadow-2xl animate-modal">
        <div className="flex items-center gap-3 border-b border-background-200 px-4 py-3.5">
          <i className="ri-search-line text-foreground-500"></i>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜一条沙雕剧情 / 关键词…"
            className="min-w-0 flex-1 bg-transparent text-base text-foreground-900 outline-none placeholder:text-foreground-400"
          />
          <button
            onClick={onClose}
            aria-label="关闭搜索"
            className="grid h-8 w-8 place-items-center rounded-full bg-background-100 text-foreground-500 hover:bg-background-200"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto p-3">
          {!q.trim() && (
            <p className="px-2 py-1.5 text-xs font-medium text-foreground-400">热门爆梗，点我直达</p>
          )}
          <div className="flex flex-col gap-1">
            {results.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-background-100 text-foreground-400">
                  <i className="ri-emotion-sad-line text-xl"></i>
                </span>
                <p className="text-sm text-foreground-600">没搜到相关爆梗，换个词试试？</p>
              </div>
            ) : (
              results.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    onOpenStory(s.id);
                    onClose();
                  }}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-background-100"
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-secondary-100 text-secondary-900 text-lg">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-foreground-900">{s.title}</span>
                    <span className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
                      <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 font-medium text-accent-800">
                        {s.tag}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {s.reads}
                      </span>
                    </span>
                  </span>
                  <i className="ri-corner-down-left-line shrink-0 text-foreground-300"></i>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}